import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const Hero = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-screen overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1681579289875-6c1b0f2129f9?w=1920&q=80"
          alt="Persian feast"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0C0C0E]" />
      </div>

      {/* Decorative Blobs */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-saffron/20 rounded-full blur-3xl animate-drift" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-pomegranate/20 rounded-full blur-3xl animate-drift" style={{ animationDelay: "-10s" }} />

      {/* Content */}
      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 pt-32 md:pt-40 pb-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-160px)]">
          {/* Left Column - Text */}
          <div className="space-y-8">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="font-accent text-saffron text-xl md:text-2xl"
            >
              Welcome to
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-white leading-[0.9]"
            >
              Cafe{" "}
              <span className="italic text-saffron">Irani</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="font-body text-white/60 text-base md:text-lg max-w-md leading-relaxed"
            >
              A modern Iranian dining experience. Savor the flavors of Iran in an atmosphere 
              that blends tradition with contemporary elegance.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex flex-wrap gap-4 pt-4"
            >
              <Link
                to="/reservations"
                data-testid="hero-book-btn"
                className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-saffron text-white font-heading italic text-lg hover:bg-saffron/90 transition-all duration-300"
              >
                Book a Table
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/menu"
                data-testid="hero-menu-btn"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-white/20 text-white font-heading italic text-lg hover:bg-white/10 transition-all duration-300"
              >
                View Menu
              </Link>
            </motion.div>
          </div>

          {/* Right Column - Overlapping Images */}
          <div className="relative hidden lg:block">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
              animate={{ opacity: 1, scale: 1, rotate: -3 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="relative z-20 w-80 h-96 ml-auto mr-12"
            >
              <img
                src="https://images.unsplash.com/photo-1603360946369-dc9bb6258143?w=600&q=80"
                alt="Kabab platter"
                className="w-full h-full object-cover rounded-2xl shadow-2xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: 6 }}
              animate={{ opacity: 1, scale: 1, rotate: 6 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="absolute top-32 -left-8 z-10 w-64 h-80"
            >
              <img
                src="https://images.unsplash.com/photo-1626200419199-391ae4be7a41?w=500&q=80"
                alt="Fesenjan"
                className="w-full h-full object-cover rounded-2xl shadow-2xl"
              />
            </motion.div>

            {/* Decorative Text Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="absolute -bottom-8 right-0 z-30"
            >
              <p className="font-accent text-saffron/40 text-6xl transform -rotate-12">
                Since 2024
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-2 text-white/40">
          <span className="font-body text-xs tracking-widest uppercase">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-white/40 to-transparent" />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
